import { useQuery } from '@tanstack/react-query';
import { useOrganization } from '@/contexts/OrganizationContext';
import { activityLogService } from '@/api/services/activity-log.service';
import type {
  ActivityLogFilterParams,
  ActivityQueryParams,
  ChildOrgsOrderedParams,
} from '@/api/types/dtos/activity-log/activity-log.dto';

export const activityLogKeys = {
  all: (orgId: string) => ['activity-log', orgId] as const,
  feed: (orgId: string, params: ActivityLogFilterParams) =>
    ['activity-log', orgId, 'feed', params] as const,
  childFeed: (orgId: string, childOrgId: string, params: ActivityLogFilterParams) =>
    ['activity-log', orgId, 'childFeed', childOrgId, params] as const,
  health: (orgId: string) => ['activity-log', orgId, 'health'] as const,
  query: (orgId: string, params: ActivityQueryParams) =>
    ['activity-log', orgId, 'query', params] as const,
  orgsOrdered: (orgId: string, params: ChildOrgsOrderedParams) =>
    ['activity-log', orgId, 'orgsOrdered', params] as const,
};

/**
 * Fetch the paginated, grouped activity feed.
 * Organization context is provided via X-Organization-Id header.
 */
export const useActivityFeed = (params: ActivityLogFilterParams = {}) => {
  const { selectedOrganization } = useOrganization();

  return useQuery({
    queryKey: activityLogKeys.feed(selectedOrganization?.id || '', params),
    queryFn: async () => {
      if (!selectedOrganization?.id) return null;
      const res = await activityLogService.getFeed(params);
      return res.data;
    },
    enabled: !!selectedOrganization?.id,
    staleTime: 0,
    refetchOnMount: 'always',
  });
};

/**
 * Fetch activity feed for a specific child org.
 * Validates child belongs to the current parent org via header.
 */
export const useChildOrgActivityFeed = (childOrgId: string | null, params: ActivityLogFilterParams = {}) => {
  const { selectedOrganization } = useOrganization();

  return useQuery({
    queryKey: activityLogKeys.childFeed(selectedOrganization?.id || '', childOrgId || '', params),
    queryFn: async () => {
      if (!selectedOrganization?.id || !childOrgId) return null;
      const res = await activityLogService.getChildOrgFeed(childOrgId, params);
      return res.data;
    },
    enabled: !!selectedOrganization?.id && !!childOrgId,
    staleTime: 30_000,
  });
};

/**
 * Fetch the aggregated health summary for the current org.
 */
export const useHealthSummary = () => {
  const { selectedOrganization } = useOrganization();

  return useQuery({
    queryKey: activityLogKeys.health(selectedOrganization?.id || ''),
    queryFn: async () => {
      if (!selectedOrganization?.id) return null;
      const res = await activityLogService.getHealthSummary();
      return res.data;
    },
    enabled: !!selectedOrganization?.id,
    staleTime: 60_000,
  });
};

/**
 * Run a pre-defined query against child organizations.
 */
export const useActivityQuery = (params: ActivityQueryParams | null) => {
  const { selectedOrganization } = useOrganization();

  return useQuery({
    queryKey: params
      ? activityLogKeys.query(selectedOrganization?.id || '', params)
      : ['activity-log', selectedOrganization?.id || '', 'query', 'idle'] as const,
    queryFn: async () => {
      if (!selectedOrganization?.id || !params) return null;
      const res = await activityLogService.queryOrgs(params);
      return res.data;
    },
    enabled: !!selectedOrganization?.id && !!params,
    staleTime: 30_000,
  });
};

/**
 * Fetch child orgs enriched with last-activity dates, ordered by most recent update.
 */
export const useChildOrgsOrdered = (params: ChildOrgsOrderedParams | undefined) => {
  const { selectedOrganization } = useOrganization();

  return useQuery({
    queryKey: activityLogKeys.orgsOrdered(selectedOrganization?.id || '', params ?? {}),
    queryFn: async () => {
      if (!selectedOrganization?.id || !params) return null;
      const res = await activityLogService.getChildOrgsOrdered(params);
      return res.data;
    },
    enabled: !!selectedOrganization?.id && !!params,
    staleTime: 30_000,
  });
};
